import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {IconButton} from '@ui/buttons/icon-button';
import {KeyboardArrowLeftIcon} from '@ui/icons/material/KeyboardArrowLeft';
import {KeyboardArrowRightIcon} from '@ui/icons/material/KeyboardArrowRight';

/**
 * Lightweight horizontal carousel — no dependency, just CSS scroll-snap plus
 * arrow buttons that scroll by one "page". Used by the photo-led blocks
 * (featured opportunities, testimonials) to make content feel interactive
 * instead of a static wall of cards.
 *
 * Works in RTL for free: it scrolls by `clientWidth` relative to the current
 * scrollLeft, and browsers already mirror scrollLeft under `dir="rtl"`.
 * Native touch/trackpad swiping works with no JS at all — the buttons are a
 * progressive enhancement for mouse users.
 */
export function Carousel({
  children,
  itemClass = 'w-[280px] sm:w-[320px]',
  className = '',
  ariaLabel,
}: {
  children: ReactNode[];
  /** Width of each slide. Slides are fixed-width so snapping is predictable. */
  itemClass?: string;
  className?: string;
  ariaLabel?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [rtl, setRtl] = useState(false);

  const sync = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    // abs() because RTL reports negative scrollLeft in some browsers.
    const pos = Math.abs(el.scrollLeft);
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(pos <= 2);
    setAtEnd(pos >= max - 2);
    setRtl(getComputedStyle(el).direction === 'rtl');
  }, []);

  useEffect(() => {
    sync();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', sync, {passive: true});
    window.addEventListener('resize', sync);
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync, children.length]);

  const page = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    const rtl = getComputedStyle(el).direction === 'rtl';
    const amount = el.clientWidth * 0.9 * dir * (rtl ? -1 : 1);
    el.scrollBy({left: amount, behavior: 'smooth'});
  };

  // Nothing to scroll through — render a plain row and skip the chrome.
  const showArrows = children.length > 1;

  // "Previous" points toward the start edge, "Next" toward the end. Which chevron
  // that is flips under RTL, so pick per direction rather than hard-coding L/R.
  const PrevIcon = rtl ? KeyboardArrowRightIcon : KeyboardArrowLeftIcon;
  const NextIcon = rtl ? KeyboardArrowLeftIcon : KeyboardArrowRightIcon;

  return (
    // md:px-48 reserves a gutter on each side so the arrows sit OUTSIDE the
    // cards instead of on top of them (the previous version overlapped the
    // first/last card). Arrows are hidden on mobile, where swipe is the norm.
    <div className={`relative md:px-48 ${className}`}>
      <div
        ref={trackRef}
        role="region"
        aria-label={ariaLabel}
        className="ap-no-scrollbar flex snap-x snap-mandatory gap-20 overflow-x-auto scroll-smooth pb-8"
      >
        {children.map((child, i) => (
          <div key={i} className={`shrink-0 snap-start ${itemClass}`}>
            {child}
          </div>
        ))}
      </div>

      {/* Arrows live in the side gutters (start-0/end-0 of the padded wrapper),
          so they sit beside the cards, never over them. Logical positioning +
          swapped chevrons keep them correct in RTL. Hidden on mobile (swipe). */}
      {showArrows && (
        // The `hidden md:block` lives on a plain WRAPPER, not on the IconButton:
        // the button's own `inline-flex` display class fights a `hidden` passed
        // via className, so the arrows kept showing on phones — where there is
        // no side gutter, which is why they sat on top of the card.
        <div className="hidden md:block">
          <IconButton
            size="md"
            variant="flat"
            radius="rounded-full"
            className={`absolute start-0 top-1/2 z-10 -translate-y-1/2 bg-paper text-main shadow-lg ring-1 ring-divider transition-opacity hover:bg-paper ${
              atStart ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
            disabled={atStart}
            onClick={() => page(-1)}
            aria-label="Previous"
          >
            <PrevIcon />
          </IconButton>
          <IconButton
            size="md"
            variant="flat"
            radius="rounded-full"
            className={`absolute end-0 top-1/2 z-10 -translate-y-1/2 bg-paper text-main shadow-lg ring-1 ring-divider transition-opacity hover:bg-paper ${
              atEnd ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
            disabled={atEnd}
            onClick={() => page(1)}
            aria-label="Next"
          >
            <NextIcon />
          </IconButton>
        </div>
      )}
    </div>
  );
}
